import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { MailOutline as MailIcon } from '@mui/icons-material';
import axios from 'axios';

interface EmailVerificationPromptProps {
  open: boolean;
  email: string;
  onClose: () => void;
}

const EmailVerificationPrompt: React.FC<EmailVerificationPromptProps> = ({
  open,
  email,
  onClose,
}) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    setSending(true);
    setError(null);
    
    try {
      await axios.post('/api/auth/resend-verification', { email });
      setSent(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'メールの送信に失敗しました';
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '600px',
          width: '90%',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MailIcon color="primary" />
          メールアドレスの確認が必要です
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          アカウントのセキュリティのため、メールアドレスの確認が必要です。
        </Typography>
        
        <Alert severity="info" sx={{ my: 2 }}>
          <Typography variant="body2">
            <strong>{email}</strong> に確認メールを送信しています。
          </Typography>
        </Alert>

        {!sent && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            メールが届いていない場合は、迷惑メールフォルダもご確認ください。
          </Typography>
        )}

        {sent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            確認メールを再送信しました。メールボックスをご確認ください。
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            確認メールが届かない場合：
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              迷惑メールフォルダを確認してください
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              登録したメールアドレスが正しいか確認してください
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              しばらく待ってから再送信してください
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          閉じる
        </Button>
        <Button
          onClick={handleResendEmail}
          variant="contained"
          disabled={sending || sent}
          startIcon={sending && <CircularProgress size={20} />}
        >
          {sending ? '送信中...' : sent ? '送信済み' : '確認メールを再送信'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationPrompt; 